/**
 * Utility function to convert technical error messages to user-friendly messages
 * @param {Error|Object} error - The error object from API or catch block
 * @param {string} defaultMessage - Default message if error can't be mapped
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyError = (error, defaultMessage = 'Something went wrong. Please try again.') => {
  // Handle different error formats
  const errorMessage = error?.message || error?.response?.data?.message || error?.response?.data?.error || '';
  const errorCode = error?.code || error?.response?.status || error?.status;
  const errorType = error?.type || error?.response?.data?.type;

  // Network errors
  if (errorType === 'network' || errorCode === 'ERR_NETWORK' || errorMessage.includes('Network Error')) {
    return 'Connection problem. Please check your internet connection and try again.';
  }

  // HTTP status code mappings
  if (errorCode) {
    switch (errorCode) {
      case 400:
      case 'ERR_BAD_REQUEST':
        if (errorMessage.toLowerCase().includes('not found') || errorMessage.toLowerCase().includes('resource not found')) {
          return 'The requested resource could not be found. It may have been removed or does not exist.';
        }
        return 'Invalid request. Please check your input and try again.';
      
      case 401:
        return 'Please log in to continue.';
      
      case 403:
        return 'You don\'t have permission to do that.';
      
      case 404:
        if (errorMessage.toLowerCase().includes('student')) {
          return 'The student record could not be found. It may have been removed.';
        }
        if (errorMessage.toLowerCase().includes('program')) {
          return 'The program could not be found. It may have been removed.';
        }
        return 'The requested resource could not be found.';
      
      case 409:
        return 'This action conflicts with existing data. Please refresh and try again.';
      
      case 422:
        return 'Invalid data provided. Please check all fields and try again.';
      
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      
      case 500:
      case 'ERR_BAD_RESPONSE':
        return 'We’re having trouble right now. Please try again in a moment.';
      
      case 502:
        return 'Service temporarily unavailable. Please try again in a moment.';
      
      case 503:
        return 'Service is currently unavailable. Please try again later.';
      
      default:
        break;
    }
  }

  // Specific error message mappings
  const lowerMessage = errorMessage.toLowerCase();
  
  if (lowerMessage.includes('failed to fetch') || lowerMessage.includes('fetch failed')) {
    return 'Unable to load data. Please check your internet connection and try again.';
  }
  
  if (lowerMessage.includes('session expired') || lowerMessage.includes('unauthorized')) {
    return 'Please log in to continue.';
  }
  
  if (lowerMessage.includes('not found')) {
    return 'The requested item could not be found. It may have been removed.';
  }
  
  if (lowerMessage.includes('validation') || lowerMessage.includes('invalid')) {
    return 'Invalid data provided. Please check all required fields and try again.';
  }
  
  if (lowerMessage.includes('permission') || lowerMessage.includes('forbidden')) {
    return 'You don\'t have permission to do that.';
  }
  
  if (lowerMessage.includes('timeout')) {
    return 'Request timed out. Please check your connection and try again.';
  }
  
  if (lowerMessage.includes('server error') || lowerMessage.includes('internal server error')) {
    return 'We’re having trouble right now. Please try again in a moment.';
  }

  // If we have a meaningful error message, use it (but make it more user-friendly)
  if (errorMessage && errorMessage !== defaultMessage) {
    // Capitalize first letter and add period if needed
    let friendlyMessage = errorMessage.trim();
    if (friendlyMessage && !friendlyMessage.endsWith('.') && !friendlyMessage.endsWith('!') && !friendlyMessage.endsWith('?')) {
      friendlyMessage += '.';
    }
    // Make first letter uppercase
    friendlyMessage = friendlyMessage.charAt(0).toUpperCase() + friendlyMessage.slice(1);
    return friendlyMessage;
  }

  // Default fallback
  return defaultMessage;
};

/**
 * Get a contextual error message based on the action being performed
 * @param {string} action - The action being performed (e.g., 'add student', 'update status')
 * @param {Error|Object} error - The error object
 * @returns {string} Contextual user-friendly error message
 */
export const getContextualError = (action, error) => {
  const baseMessage = getUserFriendlyError(error);
  
  const actionMessages = {
    'add student': 'Could not add student. Please check all required fields and try again.',
    'update status': 'Could not update student status. Please try again.',
    'update progress': 'Could not update progress. Please check the values and try again.',
    'update review': 'Could not update review. Please try again.',
    'remove student': 'Could not remove student. Please try again.',
    'send reminder': 'Could not send payment reminder. Please try again.',
    'send message': 'Could not send message. Please check your connection and try again.',
    'assign plan': 'Could not assign payment plan. Please try again.',
    'fetch students': 'Unable to load students. Please check your internet connection and try again.',
    'fetch programs': 'Unable to load programs. Please check your internet connection and try again.',
    'import students': 'Could not import students. Please check the file format and try again.',
    'invite student': 'Could not send invitation. Please check the contact information and try again.',
  };

  // If we have a specific message for this action, use it
  if (actionMessages[action.toLowerCase()]) {
    // But if the base message is more specific (e.g., session expired), use that
    if (baseMessage.includes('session') || baseMessage.includes('permission') || baseMessage.includes('connection')) {
      return baseMessage;
    }
    return actionMessages[action.toLowerCase()];
  }

  return baseMessage;
};


